/* global URL, history */
import { useEffect, useCallback } from 'react'
import { toDbParam, parseDbParam } from './db-cache.js'

const URL_SYNC_EVENT = 'deergo:url-sync'

function replaceUrl(url: URL): void {
  history.replaceState(null, '', url.toString())
  window.dispatchEvent(new window.Event(URL_SYNC_EVENT))
}

/** Parsed ?db= URL param. */
export type InitialDbParam = { id: string; fileName: string } | null

/** Read the initial ?db= param from the URL (call once on mount). */
export function readInitialDbParam(): InitialDbParam {
  const raw = new URLSearchParams(window.location.search).get('db')
  return raw ? parseDbParam(raw) : null
}

/** Read initial ?from= and ?to= params from the URL (call once after data loads). */
export function readInitialDateRange(): { from: string; to: string } | null {
  const params = new URLSearchParams(window.location.search)
  const from = params.get('from')
  const to = params.get('to')
  return from && to ? { from, to } : null
}

/**
 * Sync dtFrom/dtTo and db identity to URL search params.
 * All URL manipulation is centralized here.
 */
export function useUrlSync(dtFrom: string, dtTo: string) {
  // Sync date range to URL
  useEffect(() => {
    if (!dtFrom && !dtTo) return
    const url = new URL(window.location.href)
    if (dtFrom) url.searchParams.set('from', dtFrom)
    else url.searchParams.delete('from')
    if (dtTo) url.searchParams.set('to', dtTo)
    else url.searchParams.delete('to')
    replaceUrl(url)
  }, [dtFrom, dtTo])

  const setDbInUrl = useCallback((id: string, fileName: string) => {
    const url = new URL(window.location.href)
    url.searchParams.set('db', toDbParam(id, fileName))
    replaceUrl(url)
  }, [])

  const clearDbFromUrl = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete('db')
    replaceUrl(url)
  }, [])

  return { setDbInUrl, clearDbFromUrl }
}
